export type UtmParams = {
	utm_source?: string;
	utm_medium?: string;
	utm_campaign?: string;
	utm_term?: string;
	utm_content?: string;
};

export const UTM_PARAM_KEYS: (keyof UtmParams)[] = [
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_term",
	"utm_content",
];

export const CLICK_ID_PARAM_KEYS = [
	"gclid",
	"fbclid",
	"gbraid",
	"wbraid",
	"ad_id",
	"utm_id",
] as const;

export type ClickIdKey = (typeof CLICK_ID_PARAM_KEYS)[number];

/** Drops click-id query params. A string that is not a URL is returned as-is. */
export function urlWithoutClickIds(url: string): string {
	try {
		const parsed = new URL(url);
		for (const key of CLICK_ID_PARAM_KEYS) {
			parsed.searchParams.delete(key);
		}
		return parsed.toString();
	} catch {
		return url;
	}
}

export type ClickIdParams = Partial<Record<ClickIdKey, string>>;

export type AttributionQueryParams = Partial<UtmParams> & ClickIdParams;

export const GCLID_COOKIE = "_gcl_aw";
export const GBRAID_COOKIE = "_gcl_gb";
export const WBRAID_COOKIE = "_gcl_ag";
export const FBCLID_COOKIE = "_fbc";

// Platform cookies Google and Meta set. `ad_id` and `utm_id` have no
// equivalent; they live only in `click_id_session`.
export const CLICK_ID_COOKIE_NAMES: Partial<Record<ClickIdKey, string>> = {
	gclid: GCLID_COOKIE,
	gbraid: GBRAID_COOKIE,
	wbraid: WBRAID_COOKIE,
	fbclid: FBCLID_COOKIE,
};

export const UTM_SESSION_COOKIE = "utm_session";
export const UTM_FIRST_TOUCH_COOKIE = "utm_first_touch";
export const CLICK_ID_SESSION_COOKIE = "click_id_session";
export const INITIAL_REFERRER_COOKIE = "initial_referrer";

// Mixpanel's direct-visit sentinel for `$initial_referrer`.
export const MIXPANEL_DIRECT_REFERRER = "$direct";

// Matches GA4's default session window.
export const UTM_SESSION_TTL_SECONDS = 30 * 60;
// Chrome caps persistent cookies around 400 days. 395 matches the
// tracking-consent cookie: the longest lifetime the browser will keep.
export const UTM_FIRST_TOUCH_TTL_SECONDS = 395 * 24 * 60 * 60;

/**
 * Collect-protocol campaign fields (`cs`/`cm`/`cn`/`cc`/`ck`). The sGTM GA4
 * client copies these onto the event model used for session channel grouping.
 */
export type Ga4Campaign = {
	source?: string;
	medium?: string;
	name?: string;
	content?: string;
	term?: string;
};

const UTM_TO_CAMPAIGN: [keyof UtmParams, keyof Ga4Campaign][] = [
	["utm_source", "source"],
	["utm_medium", "medium"],
	["utm_campaign", "name"],
	["utm_content", "content"],
	["utm_term", "term"],
];

export function ga4CampaignFromUtm(
	params: UtmParams | null | undefined
): Ga4Campaign | undefined {
	if (!params) {
		return undefined;
	}
	const campaign: Ga4Campaign = {};
	for (const [utmKey, campaignKey] of UTM_TO_CAMPAIGN) {
		const value = params[utmKey];
		if (value) {
			campaign[campaignKey] = value;
		}
	}
	return Object.keys(campaign).length > 0 ? campaign : undefined;
}

export function extractUtmParams(
	searchParams: URLSearchParams
): UtmParams | null {
	const params: UtmParams = {};
	let hasAny = false;
	for (const key of UTM_PARAM_KEYS) {
		const value = searchParams.get(key);
		if (value) {
			params[key] = value;
			hasAny = true;
		}
	}
	return hasAny ? params : null;
}

export function encodeUtmCookieValue(params: UtmParams): string {
	return JSON.stringify(params);
}

export function decodeUtmCookieValue(
	value: string | undefined | null
): UtmParams | null {
	if (!value) {
		return null;
	}
	try {
		const parsed: unknown = JSON.parse(value);
		if (parsed && typeof parsed === "object") {
			return parsed as UtmParams;
		}
		return null;
	} catch {
		return null;
	}
}

/**
 * Session (last-touch) cookie value: new UTM params in the request overwrite
 * it; otherwise the existing value is preserved so an in-session page view
 * with no UTM params doesn't blank out attribution captured earlier in the
 * same session. Returns null when there's nothing to write (no existing
 * cookie and no UTM params on this request).
 */
export function resolveSessionUtmCookieValue(
	searchParams: URLSearchParams,
	existingCookieValue: string | undefined
): string | null {
	const incoming = extractUtmParams(searchParams);
	if (incoming) {
		return encodeUtmCookieValue(incoming);
	}
	return existingCookieValue ?? null;
}

/**
 * Renames first-touch UTM params to a `first_touch_` prefix so they can sit
 * alongside the (last-touch) `utm_*` properties on the same event without
 * colliding — downstream analysis needs both: which channel is credited for
 * this session vs. which channel originally acquired the visitor.
 */
export function toFirstTouchProperties(
	params: UtmParams | null
): Record<string, string> {
	if (!params) {
		return {};
	}
	const result: Record<string, string> = {};
	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined) {
			result[`first_touch_${key}`] = value;
		}
	}
	return result;
}

/**
 * First-touch cookie value: written once and never overwritten, so it keeps
 * crediting the channel that originally brought the visitor in regardless of
 * later campaign clicks in the same or a later session. Returns null once
 * the cookie already exists (nothing to write) or there's no UTM data yet.
 */
export function resolveFirstTouchUtmCookieValue(
	searchParams: URLSearchParams,
	existingCookieValue: string | undefined
): string | null {
	if (existingCookieValue) {
		return null;
	}
	const incoming = extractUtmParams(searchParams);
	return incoming ? encodeUtmCookieValue(incoming) : null;
}

export function extractClickIdParams(
	searchParams: URLSearchParams
): ClickIdParams {
	const params: ClickIdParams = {};
	for (const key of CLICK_ID_PARAM_KEYS) {
		const value = searchParams.get(key);
		if (value) {
			params[key] = value;
		}
	}
	return params;
}

/**
 * Click-id session (last-touch) cookie value: mirrors
 * `resolveSessionUtmCookieValue`'s precedence rules, but for ad click ids
 * (`gclid` and the rest of `CLICK_ID_PARAM_KEYS`, including `ad_id` and
 * `utm_id`) rather than UTM params. Kept as a separate cookie (not merged
 * into `UTM_SESSION_COOKIE`) so client-side consumers of the UTM cookie —
 * e.g. `registerUtmSuperProperties`, which spreads it straight into
 * Mixpanel super properties — don't pick up click ids by accident.
 */
export function resolveClickIdSessionCookieValue(
	searchParams: URLSearchParams,
	existingCookieValue: string | undefined
): string | null {
	const incoming = extractClickIdParams(searchParams);
	if (Object.keys(incoming).length > 0) {
		return JSON.stringify(incoming);
	}
	return existingCookieValue ?? null;
}

function comparableHost(hostname: string): string {
	return hostname.toLowerCase().replace(/^www\./, "");
}

// Cookie values are sent on every request. Keep the referring URL, including
// its query string, and fall back only when that would be oversized.
const INITIAL_REFERRER_MAX_LENGTH = 512;

function referrerWithinLimit(url: URL): string {
	const withSearch = `${url.origin}${url.pathname}${url.search}`;
	if (withSearch.length <= INITIAL_REFERRER_MAX_LENGTH) {
		return withSearch;
	}
	const pathOnly = `${url.origin}${url.pathname}`;
	if (pathOnly.length <= INITIAL_REFERRER_MAX_LENGTH) {
		return pathOnly;
	}
	return url.origin;
}

/**
 * First arrival referrer, written once. A document navigation with no cookie
 * stores an external Referer, or `$direct` when the visitor typed the URL.
 * Same-site Referers (including beacons, whose Referer is the current page)
 * do not write the cookie, so a later real landing still can.
 * Returns null when nothing should be written.
 */
export function resolveInitialReferrerCookieValue(input: {
	pageUrl: URL;
	referer: string | null;
	existingCookieValue: string | undefined;
	isDocumentNavigation: boolean;
}): string | null {
	if (!input.isDocumentNavigation || input.existingCookieValue) {
		return null;
	}
	if (!input.referer) {
		return MIXPANEL_DIRECT_REFERRER;
	}
	let refererUrl: URL;
	try {
		refererUrl = new URL(input.referer);
	} catch {
		return null;
	}
	if (
		comparableHost(refererUrl.hostname) ===
		comparableHost(input.pageUrl.hostname)
	) {
		return null;
	}
	return referrerWithinLimit(refererUrl);
}

/**
 * Event properties for a stored initial referrer. Empty when the cookie is
 * missing or not a URL / `$direct`.
 */
export function initialReferrerEventProperties(
	cookieValue: string | undefined | null
): Record<string, string> {
	if (!cookieValue) {
		return {};
	}
	if (cookieValue === MIXPANEL_DIRECT_REFERRER) {
		return {
			$initial_referrer: MIXPANEL_DIRECT_REFERRER,
			$initial_referring_domain: MIXPANEL_DIRECT_REFERRER,
		};
	}
	try {
		const url = new URL(cookieValue);
		return {
			$initial_referrer: `${url.origin}${url.pathname}${url.search}`,
			$initial_referring_domain: url.hostname,
		};
	} catch {
		return {};
	}
}

export function decodeClickIdCookieValue(
	value: string | undefined | null
): ClickIdParams | null {
	if (!value) {
		return null;
	}
	try {
		const parsed: unknown = JSON.parse(value);
		if (parsed && typeof parsed === "object") {
			return parsed as ClickIdParams;
		}
		return null;
	} catch {
		return null;
	}
}

/**
 * Google Conversion Linker cookies (`_gcl_aw`, `_gcl_gb`, `_gcl_ag`) store
 * `GCL.<timestamp>.<id>`. Multiple records are concatenated with `.GCL.`.
 */
export function parseGclCookieValue(
	value: string | undefined | null
): string | undefined {
	if (!value) {
		return undefined;
	}
	const match = /^GCL\.\d+\.(.+)$/.exec(value);
	if (!match?.[1]) {
		return undefined;
	}
	const [id] = match[1].split(".GCL.");
	return id || undefined;
}

/**
 * Meta `_fbc` cookie stores `fb.<subdomainIndex>.<creationTime>.<fbclid>`.
 */
export function parseFbcCookieValue(
	value: string | undefined | null
): string | undefined {
	if (!value) {
		return undefined;
	}
	const match = /^fb\.\d+\.\d+\.(.+)$/.exec(value);
	return match?.[1] || undefined;
}

/**
 * Merge attribution params onto a URL without clobbering existing query
 * keys or the hash. Returns the original string when nothing is added or
 * the URL cannot be parsed.
 */
export function mergeAttributionQueryParams(
	url: string,
	params: AttributionQueryParams
): string {
	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		return url;
	}

	let changed = false;
	for (const [key, value] of Object.entries(params)) {
		if (!value || parsed.searchParams.has(key)) {
			continue;
		}
		parsed.searchParams.set(key, value);
		changed = true;
	}

	return changed ? parsed.toString() : url;
}
